import React from 'react'
import ComboBox, { type ComboBoxOption } from '../../../../components/ui/combobox'
import { VoiceOptionItem } from './voice-option-item'
import type { VoiceOption } from '../../context/podcast-editor-provider'
import { useAudioPreview } from '../../hooks/use-audio-preview'
import { Skeleton } from '../../../../components/ui/skeleton'

export const VoiceSelect: React.FC<{
  label: string
  value: string
  onChange: (id: string) => void
  options: VoiceOption[]
  loading?: boolean
  labelClassName?: string
}> = ({ label, value, onChange, options, loading = false, labelClassName }) => {
  const { playingId, toggle, stop } = useAudioPreview()

  const comboOptions: Array<ComboBoxOption<string>> = React.useMemo(() => {
    // 1. No filtering by language (all Gemini voices are multilingual; the model auto-detects the input language)
    const filtered = options

    // 2. Group by Category
    const groups: Record<string, VoiceOption[]> = {
      'Recommended': [],
      'Stable': [],
      'Narration': [],
      'Other': []
    }

    filtered.forEach(o => {
      if (o.category === 'Recommended') groups['Recommended'].push(o)
      else if (o.category === 'Stable') groups['Stable'].push(o)
      else if (o.category === 'Narration') groups['Narration'].push(o)
      else groups['Other'].push(o)
    })

    // Sort General/Other voices by previewUrl presence
    groups['Other'].sort((a, b) => {
      if (a.previewUrl && !b.previewUrl) return -1
      if (!a.previewUrl && b.previewUrl) return 1
      return 0
    })

    // Sort order: Recommended -> Narration -> Stable -> Other
    const sorted = [
      ...groups['Recommended'],
      ...groups['Narration'],
      ...groups['Stable'],
      ...groups['Other']
    ]

    return sorted.map(o => {
      const keywords: string[] = []
      if (o.gender) keywords.push(o.gender)
      if (o.category) keywords.push(o.category)

      return {
        value: o.id,
        label: o.name || o.id,
        keywords: keywords.length > 0 ? keywords : undefined,
        data: o
      }
    })
  }, [options])

  if (loading) {
    return (
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Skeleton className="h-4 w-24" />
        </div>
        <div className="h-10 rounded border border-black/10 bg-white p-2">
          <Skeleton className="h-4 w-32" />
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <label className={labelClassName ?? 'text-sm font-medium text-foreground'}>{label}</label>
      </div>
      <ComboBox
        value={value}
        onChange={onChange}
        options={comboOptions}
        placeholder="Select a voice"
        onOpenChange={(open) => {
          if (!open) {
            stop()
          }
        }}
        renderButtonContent={(sel) => (
          <div className="flex items-center gap-2 truncate w-full">
            <span className="truncate">{sel?.label ?? 'Select a voice'}</span>
          </div>
        )}
        renderOption={(opt) => {
          const meta = opt.data as VoiceOption
          const isPlaying = playingId === opt.value
          return (
            <VoiceOptionItem
              optionId={opt.value}
              meta={meta}
              isPlaying={!!isPlaying}
              onToggle={(e, id, url) => {
                e.stopPropagation()
                void toggle(id, url)
              }}
            />
          )
        }}
      />
    </div>
  )
}
